import { useMemo, useState } from 'react';
import { X, Gift, Trophy } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { lamportsToSol } from '@/utils/solana/lamportsToSol';
import { useUnclaimedRewards, useClaimLotteryReward, useMintSeasonNFT } from '@/hooks/usePointsLottery';
import type { UnclaimedRewardItem } from '@/types/schemas';
import Loading from '@/components/Loading';
import { LotteryClaimSuccessModal } from '@/components/LotteryClaimSuccessModal';
import { TweetVerificationModal } from '@/components/TweetVerificationModal';

const NY_LOTTERY_SEASON_NO = 0;

interface HistoricalRewardsModalProps {
  open: boolean;
  onClose: () => void;
  onMintSuccess?: () => void;
}

function formatNumber(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value;
  return num.toLocaleString();
}

export function HistoricalRewardsModal({
  open,
  onClose,
  onMintSuccess,
}: HistoricalRewardsModalProps) {
  const queryClient = useQueryClient();
  const { data: unclaimedItems, isLoading, refetch } = useUnclaimedRewards();
  const claimMutation = useClaimLotteryReward({ showToast: false });
  const mintMutation = useMintSeasonNFT();
  const [processingSeasonId, setProcessingSeasonId] = useState<number | null>(null);
  const [claimedReward, setClaimedReward] = useState<{
    prizeLabel: string;
    prizeAmountLamports: string;
    seasonNo: number;
  } | null>(null);

  // State for tweet verification
  const [tweetVerificationOpen, setTweetVerificationOpen] = useState(false);
  const [selectedReward, setSelectedReward] = useState<UnclaimedRewardItem | null>(null);

  const nyItems = useMemo(() => {
    return (unclaimedItems ?? []).filter(
      (item) =>
        item.seasonNo === NY_LOTTERY_SEASON_NO && item.unclaimedReward != null
    );
  }, [unclaimedItems]);

  const displayItems = useMemo(() => {
    return (unclaimedItems ?? []).filter(
      (item) => item.seasonNo !== NY_LOTTERY_SEASON_NO
    );
  }, [unclaimedItems]);

  const handleClaimReward = (item: UnclaimedRewardItem) => {
    if (!item.unclaimedReward) return;

    // Check if user is already verified
    const isVerified = item.unclaimedReward.isVerified;
    const canClaim = item.unclaimedReward.canClaim;

    console.log('[HistoricalRewardsModal] CLAIM REWARD CLICKED:', {
      seasonId: item.seasonId,
      winnerId: item.unclaimedReward.winnerId,
      isVerified,
      canClaim,
    });

    // If already verified and can claim, claim directly
    if (isVerified === true && canClaim === true) {
      console.log('[HistoricalRewardsModal] User already verified - claiming directly');
      claimDirectly(item);
    } else {
      // Otherwise, show verification modal first
      console.log('[HistoricalRewardsModal] User needs verification - showing modal');
      setSelectedReward(item);
      setTweetVerificationOpen(true);
    }
  };

  const claimDirectly = async (item: UnclaimedRewardItem) => {
    if (!item.unclaimedReward) return;

    setProcessingSeasonId(item.seasonId);
    try {
      await claimMutation.mutateAsync(item.seasonId);
      await refetch();

      // Show success modal with the claimed reward details
      setClaimedReward({
        prizeLabel: item.unclaimedReward.prizeLabel,
        prizeAmountLamports: item.unclaimedReward.prizeAmountLamports,
        seasonNo: item.seasonNo,
      });
    } finally {
      setProcessingSeasonId(null);
    }
  };

  const handleMintNFT = async (item: UnclaimedRewardItem) => {
    setProcessingSeasonId(item.seasonId);
    try {
      await mintMutation.mutateAsync({ seasonNo: item.seasonNo });
      await refetch();
      if (onMintSuccess) {
        onMintSuccess();
      }
    } finally {
      setProcessingSeasonId(null);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          className={cn(
            'relative w-full max-w-3xl max-h-[80vh]',
            'bg-card rounded-xl shadow-2xl overflow-hidden'
          )}
          style={{ backgroundColor: '#011824' }}
        >
        {/* Header */}
        <div
          className="sticky top-0 z-10 p-6 border-b-2"
          style={{
            borderColor: '#50E4F9',
            background: 'linear-gradient(135deg, rgba(80, 228, 249, 0.2), rgba(80, 228, 249, 0.1))',
          }}
        >
          <button
            onClick={onClose}
            className={cn(
              'absolute top-4 right-4 p-1 rounded-full',
              'text-white/60 hover:text-white',
              'hover:bg-white/10 transition-colors'
            )}
          >
            <X size={20} />
          </button>

          <h2
            className="text-2xl font-bold"
            style={{ fontFamily: 'Outfit, sans-serif', color: '#50E4F9' }}
          >
            HISTORICAL REWARDS
          </h2>
          <p className="text-white/60 text-sm mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Claim your unclaimed rewards and NFTs from past seasons
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 120px)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loading className="w-12 h-12 text-[#50E4F9]" />
            </div>
          ) : (
            <div className="space-y-4">
              {nyItems.length > 0 && (
                <div
                  className="p-4 rounded-xl border"
                  style={{
                    borderColor: 'rgba(251, 191, 36, 0.45)',
                    backgroundColor: 'rgba(251, 191, 36, 0.10)',
                    fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  <div className="text-white font-semibold mb-1">
                    New Year Campaign rewards
                  </div>
                  <div className="text-white/70 text-sm">
                    New Year rewards are claimed on the New Year Campaign page.
                    {' '}
                    <a
                      href="/new-year-campaign"
                      className="underline text-white hover:opacity-80"
                    >
                      Go to New Year Campaign
                    </a>
                  </div>
                </div>
              )}

              {displayItems.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="text-white/60 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    No unclaimed rewards or NFTs
                  </div>
                  <p className="text-white/40 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    You're all caught up! Check back after the next season ends.
                  </p>
                </div>
              ) : (
                displayItems.map((item) => (
                  <div
                    key={item.seasonId}
                    className="p-5 rounded-xl border-2 transition-all hover:border-[#50E4F9]/60"
                    style={{
                      borderColor: '#50E4F9',
                      backgroundColor: 'rgba(26, 26, 26, 0.8)',
                    }}
                  >
                  {/* Season Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3
                        className="text-xl font-bold mb-1"
                        style={{ fontFamily: 'Outfit, sans-serif', color: '#50E4F9' }}
                      >
                        Season {item.seasonNo}
                      </h3>
                      <div className="text-sm text-white/60" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatNumber(item.pointsContributed)} points contributed
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {item.unclaimedReward && (
                        <div
                          className="px-2 py-1 rounded text-xs font-bold"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            backgroundColor: 'rgba(254, 187, 1, 0.2)',
                            color: '#FEBB01',
                          }}
                        >
                          REWARD
                        </div>
                      )}
                      {item.nftNotMinted && (
                        <div
                          className="px-2 py-1 rounded text-xs font-bold"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            backgroundColor: 'rgba(80, 228, 249, 0.2)',
                            color: '#50E4F9',
                          }}
                        >
                          NFT
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Unclaimed Reward */}
                  {item.unclaimedReward && (
                    <div
                      className="mb-3 p-3 rounded-lg border"
                      style={{
                        borderColor: '#FEBB01',
                        backgroundColor: 'rgba(254, 187, 1, 0.1)',
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs text-white/60 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Unclaimed Prize
                          </div>
                          <div className="text-sm text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {item.unclaimedReward.prizeLabel}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <img src="/solana-sol-logo.svg" alt="SOL" width={20} height={20} />
                          <span
                            className="text-lg font-bold"
                            style={{ fontFamily: 'Outfit, sans-serif', color: '#FEBB01' }}
                          >
                            {lamportsToSol(BigInt(item.unclaimedReward.prizeAmountLamports)).toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {item.unclaimedReward && (
                      <button
                        onClick={() => handleClaimReward(item)}
                        disabled={processingSeasonId === item.seasonId}
                        className="flex-1 px-4 py-2.5 rounded-full font-bold text-white transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          background: 'linear-gradient(250deg, #7239EE 16.62%, #C374F9 71.51%)',
                        }}
                      >
                        {processingSeasonId === item.seasonId ? (
                          <Loading className="inline w-4 h-4 mr-2" />
                        ) : (
                          <Gift className="inline mr-2" size={18} />
                        )}
                        {item.unclaimedReward.isVerified && item.unclaimedReward.canClaim ? 'CLAIM REWARD' : item.unclaimedReward.canClaim === false ? 'VIEW REWARD' : 'CLAIM REWARD'}
                      </button>
                    )}

                    {item.nftNotMinted && (
                      <button
                        onClick={() => handleMintNFT(item)}
                        disabled={processingSeasonId === item.seasonId}
                        className="flex-1 px-4 py-2.5 font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: 'Outfit, sans-serif',
                          borderRadius: '100px',
                          border: '5px solid #50E4F9',
                          boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                          backgroundColor: 'transparent',
                          color: '#FFFFFF',
                        }}
                      >
                        {processingSeasonId === item.seasonId ? (
                          <Loading className="inline w-4 h-4 mr-2" />
                        ) : (
                          <Trophy className="inline mr-2" size={18} />
                        )}
                        MINT NFT
                      </button>
                    )}
                  </div>
                </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Success Modal for claimed rewards */}
      {claimedReward && (
        <LotteryClaimSuccessModal
          open={!!claimedReward}
          onClose={() => setClaimedReward(null)}
          reward={claimedReward}
        />
      )}

      {/* Tweet Verification Modal - Required for claiming lottery rewards */}
      {selectedReward && selectedReward.unclaimedReward && (
        <TweetVerificationModal
          isOpen={tweetVerificationOpen}
          onClose={() => {
            setTweetVerificationOpen(false);
            setSelectedReward(null);
          }}
          type="lottery"
          winnerId={selectedReward.unclaimedReward.winnerId}
          seasonId={selectedReward.seasonId}
          onSuccess={() => {
            setTweetVerificationOpen(false);
            // Invalidate lottery history to refresh winner status
            queryClient.invalidateQueries({
              queryKey: ['pointsLottery', 'history'],
            });
            // Refetch unclaimed rewards
            refetch();
            // Now claim the reward directly
            if (selectedReward) {
              claimDirectly(selectedReward);
            }
            setSelectedReward(null);
          }}
        />
      )}
    </>
  );
}
